/**
 * KitchenManagerDashboard — Manager overview for ghost kitchen operations.
 *
 * Shows orders, station status, compliance tasks, and analytics.
 * Follows DRR-055: Dumb Frontend — all data from backend.
 */

import { useMemo, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Paper,
  SimpleGrid,
  Card,
  Badge,
  Button,
  ThemeIcon,
  Loader,
  Alert,
  Tabs,
  Progress,
  RingProgress,
  ActionIcon,
  Tooltip,
  Table,
} from '@mantine/core';
import {
  IconChefHat,
  IconFlame,
  IconThermometer,
  IconClipboardCheck,
  IconTruck,
  IconAlertTriangle,
  IconRefresh,
  IconChartBar,
  IconUsers,
  IconClock,
  IconCheck,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import {
  useFoodServiceKitchenMetricsQuery,
  useFoodServicePendingQcQuery,
  useFoodServiceReadyForPickupQuery,
  useFoodServiceAvailableDriversQuery,
  FoodServiceStation,
} from '@/generated/graphql';

export function KitchenManagerDashboard() {
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;

  // Query kitchen metrics using Food Service API
  const {
    data: metricsData,
    loading: metricsLoading,
    refetch: refetchMetrics,
  } = useFoodServiceKitchenMetricsQuery({
    skip,
    pollInterval: 15000,
  });

  // Query orders pending QC
  const {
    data: qcData,
    loading: qcLoading,
  } = useFoodServicePendingQcQuery({
    skip,
    pollInterval: 15000,
  });

  // Query orders ready for pickup
  const {
    data: readyData,
    loading: readyLoading,
  } = useFoodServiceReadyForPickupQuery({
    skip,
    pollInterval: 15000,
  });

  // Query available drivers
  const {
    data: driversData,
    loading: driversLoading,
  } = useFoodServiceAvailableDriversQuery({
    skip,
    pollInterval: 30000,
  });

  const metrics = metricsData?.kitchenMetrics;
  const pendingQc = qcData?.pendingQc ?? [];
  const readyForPickup = readyData?.readyForPickup ?? [];
  const availableDrivers = driversData?.availableDrivers ?? [];

  // Calculate station utilization from metrics
  const stationUtilization = useMemo(
    () => metrics?.stationUtilization ?? [],
    [metrics],
  );

  const handleRefresh = useCallback(() => {
    refetchMetrics();
  }, [refetchMetrics]);

  if (metricsLoading) {
    return (
      <Container size="xl" py="xl">
        <Group justify="center">
          <Loader size="lg" />
          <Text>Loading kitchen dashboard...</Text>
        </Group>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <ThemeIcon size="xl" color="blue" variant="light">
              <IconChefHat size={28} />
            </ThemeIcon>
            <div>
              <Title order={2}>Kitchen Manager</Title>
              <Text size="sm" c="dimmed">
                Overview of all kitchen operations
              </Text>
            </div>
          </Group>
          <Tooltip label="Refresh data">
            <ActionIcon variant="light" size="lg" onClick={handleRefresh}>
              <IconRefresh size={20} />
            </ActionIcon>
          </Tooltip>
        </Group>

        {/* Key Metrics */}
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
          <Card withBorder padding="md">
            <Group justify="space-between">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase">
                  Orders Today
                </Text>
                <Text size="xl" fw={700}>
                  {metrics?.ordersToday ?? 0}
                </Text>
              </div>
              <ThemeIcon color="blue" size="lg" variant="light">
                <IconClipboardCheck size={20} />
              </ThemeIcon>
            </Group>
          </Card>

          <Card withBorder padding="md">
            <Group justify="space-between">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase">
                  In Progress
                </Text>
                <Text size="xl" fw={700}>
                  {metrics?.ordersInProgress ?? 0}
                </Text>
              </div>
              <ThemeIcon color="yellow" size="lg" variant="light">
                <IconClock size={20} />
              </ThemeIcon>
            </Group>
          </Card>

          <Card withBorder padding="md">
            <Group justify="space-between">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase">
                  Avg Prep Time
                </Text>
                <Text size="xl" fw={700}>
                  {metrics?.avgPrepTimeMinutes ?? 0}m
                </Text>
              </div>
              <ThemeIcon color="green" size="lg" variant="light">
                <IconCheck size={20} />
              </ThemeIcon>
            </Group>
          </Card>

          <Card withBorder padding="md">
            <Group justify="space-between">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase">
                  Drivers Online
                </Text>
                <Text size="xl" fw={700}>
                  {availableDrivers.length}
                </Text>
              </div>
              <ThemeIcon color="teal" size="lg" variant="light">
                <IconTruck size={20} />
              </ThemeIcon>
            </Group>
          </Card>
        </SimpleGrid>

        {/* Tabs for different views */}
        <Tabs defaultValue="orders">
          <Tabs.List>
            <Tabs.Tab value="orders" leftSection={<IconClipboardCheck size={16} />}>
              Orders
            </Tabs.Tab>
            <Tabs.Tab value="stations" leftSection={<IconFlame size={16} />}>
              Stations
            </Tabs.Tab>
            <Tabs.Tab value="compliance" leftSection={<IconThermometer size={16} />}>
              Compliance
            </Tabs.Tab>
            <Tabs.Tab value="drivers" leftSection={<IconTruck size={16} />}>
              Drivers
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="orders" pt="md">
            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
              <Paper p="md" withBorder>
                <Group justify="space-between" mb="md">
                  <Title order={4}>Pending QC</Title>
                  <Badge color="yellow" size="lg">{pendingQc.length}</Badge>
                </Group>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Order</Table.Th>
                      <Table.Th>Brand</Table.Th>
                      <Table.Th>Items</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {pendingQc.slice(0, 5).map((order) => (
                      <Table.Tr key={order.id}>
                        <Table.Td>
                          <Text fw={500}>#{order.orderNumber}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge color="blue" variant="light">{order.brand}</Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{order.items.length} items</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
              <Paper p="md" withBorder>
                <Group justify="space-between" mb="md">
                  <Title order={4}>Ready for Pickup</Title>
                  <Badge color="green" size="lg">{readyForPickup.length}</Badge>
                </Group>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Order</Table.Th>
                      <Table.Th>Brand</Table.Th>
                      <Table.Th>Customer</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {readyForPickup.slice(0, 5).map((order) => (
                      <Table.Tr key={order.id}>
                        <Table.Td>
                          <Text fw={500}>#{order.orderNumber}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge color="blue" variant="light">{order.brand}</Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{order.customerName}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            </SimpleGrid>
          </Tabs.Panel>

          <Tabs.Panel value="stations" pt="md">
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
              {stationUtilization.map((station) => (
                <Card key={station.station} withBorder padding="md">
                  <Group justify="space-between" mb="md">
                    <Text fw={500}>{station.station}</Text>
                    <Badge color={station.ordersInQueue > 3 ? 'red' : station.ordersInQueue > 1 ? 'yellow' : 'green'}>
                      {station.ordersInQueue > 3 ? 'Busy' : station.ordersInQueue > 0 ? 'Active' : 'Idle'}
                    </Badge>
                  </Group>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Queue
                      </Text>
                      <Text size="sm">{station.ordersInQueue} orders</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Avg Time
                      </Text>
                      <Text size="sm">{Math.round(station.avgTimeSeconds / 60)} min</Text>
                    </Group>
                  </Stack>
                </Card>
              ))}
              {stationUtilization.length === 0 && (
                <Card withBorder padding="md">
                  <Text c="dimmed" ta="center">No station data available</Text>
                </Card>
              )}
            </SimpleGrid>
          </Tabs.Panel>

          <Tabs.Panel value="compliance" pt="md">
            <Paper p="md" withBorder>
              <Title order={4} mb="md">
                HACCP Compliance
              </Title>
              <Alert color="blue" variant="light">
                Run HACCP report from the admin menu to view compliance data.
                Temperature logs and sanitation records are tracked automatically.
              </Alert>
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="drivers" pt="md">
            <Paper p="md" withBorder>
              <Group justify="space-between" mb="md">
                <Title order={4}>Driver Status</Title>
                <Badge color="teal" size="lg">{availableDrivers.length} online</Badge>
              </Group>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Driver</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Distance</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {availableDrivers.slice(0, 5).map((driver) => (
                    <Table.Tr key={driver.id}>
                      <Table.Td>
                        <Group gap="xs">
                          <IconUsers size={16} />
                          <Text size="sm">{driver.name}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={driver.currentDeliveryId ? 'yellow' : 'green'} size="sm">
                          {driver.currentDeliveryId ? 'On Delivery' : 'Available'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{driver.distanceMiles?.toFixed(1) ?? '—'} mi</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              {availableDrivers.length === 0 && (
                <Text c="dimmed" ta="center" py="xl">
                  No drivers online
                </Text>
              )}
            </Paper>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}

export default KitchenManagerDashboard;
